import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Switch from '@/v2/components/common/Switch'
import TextField from '@/v2/components/common/TextField'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { camelCase } from '@/v2/utils/forms'
import {
  useSettingsReschedule,
  useUpdateReschedule,
} from '@/v2/hooks/settings/useSettingsReschedule'
import {
  SettingsPage,
  SettingsEmptyState,
  Fieldset,
  FieldsetTitle,
  Row,
  PrimaryButton,
} from '@/v2/components/settings/shared'
import LinkMessageEditor from '@/v2/components/settings/reschedule/LinkMessageEditor'
import ConfirmationMessageEditor from '@/v2/components/settings/reschedule/ConfirmationMessageEditor'
import { hasBlockingProblem } from '@/v2/components/settings/reschedule/linkMessageTemplate'

const LIMITS = {
  aiContextMessagesCount: { min: 1, max: 50 },
  scheduleHorizonDays: { min: 1, max: 90 },
  linkValidForHours: { min: 1, max: 168 },
}

const ERROR_COLOR = '#dc2626'

const Note = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.5;
`

const Banner = styled.div`
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  background-color: #fef3c7;
  color: #92400e;
  font-size: 0.8rem;
  line-height: 1.5;
`

const FieldError = styled.div`
  font-size: 0.75rem;
  color: ${ERROR_COLOR};
  margin-top: 0.25rem;
`

const KeywordList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`

const Keyword = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  background-color: #f3f4f6;
  color: #374151;
  font-size: 0.8rem;
`

const RemoveKeyword = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: ${ERROR_COLOR};
  }
`

const KeywordEntry = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 24rem;
`

const AddKeyword = styled.button`
  padding: 0;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
  background: none;
  color: #2563eb;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
`

const EDITABLE_FIELDS = [
  'enabled',
  'autoReplyEnabled',
  'aiEnabled',
  'autoReplyKeywords',
  'aiContextMessagesCount',
  'quietHoursStartAt',
  'quietHoursStopsAt',
  'linkMessageTemplate',
  'confirmationEnabled',
  'confirmationMessageTemplate',
  'scheduleHorizonDays',
  'linkValidForHours',
]

const toForm = settings =>
  EDITABLE_FIELDS.reduce((form, field) => {
    form[field] = settings[field]
    return form
  }, {})

// A number box is held as a string while it is being typed in, so the copy that
// quotes it falls back to what is stored rather than reading "the next  days".
const spoken = (value, fallback) => (value === '' ? fallback : value)

const Reschedule = () => {
  const { settings, isLoading, isError } = useSettingsReschedule()
  const { updateReschedule, isPending: saving } = useUpdateReschedule()
  const { onOpenSnackbar } = useSnackbarContext()

  const [form, setForm] = useState(null)
  const [keywordDraft, setKeywordDraft] = useState('')
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (settings) setForm(toForm(settings))
  }, [settings])

  const setField = (field, value) => {
    setForm(current => ({ ...current, [field]: value }))
    setErrors(current => ({ ...current, [field]: null }))
  }

  const setNumber = (field, value) => {
    // Kept as a string while the box is being typed in, so clearing it does not
    // snap back to a zero the clinic never asked for.
    setField(field, value === '' ? '' : Number(value))
  }

  const addKeyword = () => {
    const keyword = keywordDraft.trim().toLowerCase()
    if (!keyword) return
    if (!form.autoReplyKeywords.includes(keyword)) {
      setField('autoReplyKeywords', [...form.autoReplyKeywords, keyword])
    }
    setKeywordDraft('')
  }

  const removeKeyword = keyword =>
    setField(
      'autoReplyKeywords',
      form.autoReplyKeywords.filter(current => current !== keyword)
    )

  const handleSave = async () => {
    // The confirmation text is required on purpose, so that switching the
    // receipt off never costs the clinic the words it wrote. An empty box is a
    // 422; saying so here keeps the answer next to the box that caused it.
    if (!form.confirmationMessageTemplate?.trim()) {
      setErrors({
        confirmationMessageTemplate:
          "The confirmation message can't be empty. To stop sending it, turn the switch off instead.",
      })
      return
    }

    // An empty box, or a name with no value in its situation, would text an
    // empty message or a gap. The API cannot tell: both are valid Liquid.
    if (hasBlockingProblem(form.linkMessageTemplate)) {
      setErrors({
        linkMessageTemplate:
          'Some situations need fixing before you can save: pick the ones marked "needs fixing".',
      })
      return
    }

    setErrors({})

    try {
      await updateReschedule(form)
      onOpenSnackbar('Reschedule settings saved', 'success')
    } catch (error) {
      // Error keys arrive in snake_case; the form is keyed in camelCase.
      const fieldErrors = Object.entries(error.fieldErrors || {}).reduce(
        (mapped, [key, messages]) => {
          mapped[camelCase(key)] = [].concat(messages).join('; ')
          return mapped
        },
        {}
      )

      setErrors(fieldErrors)
      onOpenSnackbar(error.message, 'error')
    }
  }

  // The endpoint is admin only and answers a non-admin with an empty 404, which
  // is indistinguishable here from the screen failing to load. Both end up as
  // the same sentence, because both mean the same thing to whoever is looking:
  // there is nothing to edit. Without this a non-admin got an endless spinner and no sentence at all.
  if (isError) {
    return (
      <SettingsPage title="Rescheduling">
        <SettingsEmptyState
          heading="We couldn't open these settings"
          description="Rescheduling is set up by an admin of the clinic. If you are one, reload the page and try again."
        />
      </SettingsPage>
    )
  }

  if (isLoading || !form) {
    return <SettingsPage title="Rescheduling" loading />
  }

  const { aiAvailable } = settings
  const aiLocked = !aiAvailable && !form.aiEnabled
  const horizonDays = spoken(
    form.scheduleHorizonDays,
    settings.scheduleHorizonDays
  )
  const contextMessages = spoken(
    form.aiContextMessagesCount,
    settings.aiContextMessagesCount
  )

  return (
    <SettingsPage
      title="Rescheduling"
      subtitle="Let a patient move their own appointment through a personal link"
      actions={
        <PrimaryButton onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save changes'}
        </PrimaryButton>
      }
    >
      {!form.enabled && (
        <Banner>
          Rescheduling by link is off. You can set everything up here and turn
          it on when you are ready.
        </Banner>
      )}

      <Fieldset>
        <FieldsetTitle>Rescheduling by link</FieldsetTitle>
        <Note>
          Lets your team send a patient a personal link to move their own
          appointment. With this on, every conversation gets a{' '}
          <strong>Send reschedule link</strong> button next to the reply box: it
          writes the message for you and someone on your team presses Send —
          nothing goes out on its own.
        </Note>
        <Note>
          Turning this off removes the button and stops the automatic reply
          below. Links you already sent keep working until they are used or
          expire.
        </Note>
        <Switch
          label="Rescheduling by link"
          checked={form.enabled}
          onChange={event => setField('enabled', event.target.checked)}
        />
        {form.enabled && (
          <Note style={{ margin: '0.5rem 0 0' }}>
            The button prepares a link when the conversation is linked to one
            patient, that patient has an upcoming appointment, and there is a
            free time to offer within the next {horizonDays} days. When
            something is missing it says which.
          </Note>
        )}
        {errors.enabled && <FieldError>{errors.enabled}</FieldError>}
      </Fieldset>

      <Fieldset>
        <FieldsetTitle>Automatic reply</FieldsetTitle>
        <Note>
          On top of the button, answer the patient automatically when they text
          asking to reschedule. The same message, sent without anyone touching
          it.
        </Note>
        <Note>
          It only answers when the message matches one of the keywords below,
          the conversation has one patient, that patient has exactly one
          upcoming appointment, and there is a time to offer. Anything else —
          someone from your team already replied, the patient proposed a time,
          the patient wants to cancel — goes to your team as usual. One
          automatic answer per conversation per day.
        </Note>
        <Note>
          During quiet hours the answer waits and goes out when they end.
          Turning this off does not remove the{' '}
          <strong>Send reschedule link</strong> button.
        </Note>
        <Switch
          label="Reply automatically"
          checked={form.autoReplyEnabled}
          onChange={event => setField('autoReplyEnabled', event.target.checked)}
        />
      </Fieldset>

      <Fieldset>
        <FieldsetTitle>Keywords</FieldsetTitle>
        <Note>
          Matched anywhere in an incoming message, ignoring case, with no
          outside call. <strong>resched</strong> already covers reschedule,
          rescheduled and rescheduling; the hyphenated spelling needs its own
          entry. Saving replaces the whole list, and it cannot be left empty —
          switch the automatic reply off instead.
        </Note>
        <KeywordList>
          {form.autoReplyKeywords.map(keyword => (
            <Keyword key={keyword}>
              {keyword}
              <RemoveKeyword
                type="button"
                aria-label={`Remove ${keyword}`}
                onClick={() => removeKeyword(keyword)}
              >
                ×
              </RemoveKeyword>
            </Keyword>
          ))}
        </KeywordList>
        <KeywordEntry>
          <TextField
            fullWidth
            size="small"
            placeholder="Add a keyword"
            value={keywordDraft}
            onChange={setKeywordDraft}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault()
                addKeyword()
              }
            }}
          />
          <AddKeyword type="button" onClick={addKeyword}>
            + Add
          </AddKeyword>
        </KeywordEntry>
        {errors.autoReplyKeywords && (
          <FieldError>{errors.autoReplyKeywords}</FieldError>
        )}
      </Fieldset>

      <Fieldset>
        <FieldsetTitle>Quiet hours</FieldsetTitle>
        <Note>
          A reply that would land inside this window is held back and sent when
          it closes, not dropped. Wall clock in the clinic time zone, and it may
          cross midnight.
        </Note>
        <Row $columns="1fr 1fr 2fr">
          <TextField
            fullWidth
            type="time"
            label="From"
            value={form.quietHoursStartAt}
            onChange={value => setField('quietHoursStartAt', value)}
            error={errors.quietHoursStartAt}
          />
          <TextField
            fullWidth
            type="time"
            label="Until"
            value={form.quietHoursStopsAt}
            onChange={value => setField('quietHoursStopsAt', value)}
            error={errors.quietHoursStopsAt}
          />
        </Row>
      </Fieldset>

      <Fieldset>
        <FieldsetTitle>The message</FieldsetTitle>
        <LinkMessageEditor
          value={form.linkMessageTemplate}
          onChange={value => setField('linkMessageTemplate', value)}
          error={errors.linkMessageTemplate}
        />
      </Fieldset>

      <Fieldset>
        <FieldsetTitle>The link</FieldsetTitle>
        <Row $columns="1fr 1fr 1fr">
          <TextField
            fullWidth
            type="number"
            label="Offer times up to (days ahead)"
            inputProps={LIMITS.scheduleHorizonDays}
            value={form.scheduleHorizonDays}
            onChange={value => setNumber('scheduleHorizonDays', value)}
            error={errors.scheduleHorizonDays}
          />
          <TextField
            fullWidth
            type="number"
            label="Link stays valid for (hours)"
            inputProps={LIMITS.linkValidForHours}
            value={form.linkValidForHours}
            onChange={value => setNumber('linkValidForHours', value)}
            error={errors.linkValidForHours}
          />
        </Row>
      </Fieldset>

      <Fieldset>
        <FieldsetTitle>Confirmation message</FieldsetTitle>
        <Note>
          Seconds after a patient picks a new time on their link, one message
          goes back to them confirming it, and your team sees it in the thread —
          so a request that used to stop at the link no longer reads as
          unanswered.
        </Note>
        <Note>
          It answers something the patient just did, so it ignores quiet hours
          and does not count towards the one automatic answer per day. It is
          also sent for a link that is still in a patient&apos;s hands after you
          turn rescheduling by link off.
        </Note>
        <Switch
          label="Confirm the new time"
          checked={form.confirmationEnabled}
          onChange={event =>
            setField('confirmationEnabled', event.target.checked)
          }
        />
        {errors.confirmationEnabled && (
          <FieldError>{errors.confirmationEnabled}</FieldError>
        )}
        <Note style={{ margin: '1rem 0 0.5rem' }}>
          Turning the confirmation off is the switch above, never emptying this
          box — the message stays here for when you turn it back on.
        </Note>
        <ConfirmationMessageEditor
          value={form.confirmationMessageTemplate}
          onChange={value => setField('confirmationMessageTemplate', value)}
          error={errors.confirmationMessageTemplate}
        />
      </Fieldset>

      <Fieldset>
        <FieldsetTitle>AI refinement</FieldsetTitle>
        <Note>
          Detection always runs on the keywords above, with no outside call.
          With this on, a model also reads the last {contextMessages} messages
          to hold the answer back when the patient is not really asking to
          reschedule. It never writes to the patient. If it fails or is
          unavailable, the keyword answer stands.
        </Note>
        <Switch
          label="Refine detection with AI"
          checked={form.aiEnabled}
          disabled={aiLocked}
          onChange={event => setField('aiEnabled', event.target.checked)}
        />
        {aiLocked && <Note>Not available on this installation.</Note>}
        {errors.aiEnabled && <FieldError>{errors.aiEnabled}</FieldError>}
        <Row $columns="1fr 2fr">
          <TextField
            fullWidth
            type="number"
            label="Recent messages read"
            inputProps={LIMITS.aiContextMessagesCount}
            value={form.aiContextMessagesCount}
            onChange={value => setNumber('aiContextMessagesCount', value)}
            error={errors.aiContextMessagesCount}
          />
        </Row>
      </Fieldset>
    </SettingsPage>
  )
}

export default Reschedule
